/* eslint-disable no-cond-assign */

import React from 'react';
import { EditorState, RichUtils, genKey } from 'draft-js';
import axios from 'axios';
import OnedriveLink from 'Shared/components/SmartLink/OnedriveLink';
import FigmaLink from 'Shared/components/SmartLink/FigmaLink';
import HubspotLink from 'Shared/components/SmartLink/HubspotLink';
import SalesForceLink from 'Shared/components/SmartLink/SalesForceLink';
import GoogleDocsLink from 'Shared/components/SmartLink/GoogleDocsLink';
import GoogleSheetsLink from 'Shared/components/SmartLink/GoogleSheetsLink';
import GooglePresentationLink from 'Shared/components/SmartLink/GooglePresentationLink';
import JiraIssueLink from 'Shared/components/SmartLink/JiraIssueLink';
import AsanaTaskLink from 'Shared/components/SmartLink/AsanaTaskLink';
import { SECTION_HEADER_TITLE_TYPE } from 'Shared/components/DraftEditor/utils';

import {
  createEmptySelectionState,
  contentStateWithReplacedText,
  contentStateWithInsertedText,
  getContentStateWithAppliedEntity,
} from 'Shared/utils/draftJsHelpers';

const smartLinksSchema = [
  {
    linkAnchor: null,
    entityType: 'SALESFORCE_SMART_LINK',
    urlRegexString: '[^ ]*salesforce.com[^ ]*|[^ ]*lightning.force.com[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'GOOGLE_DOCS_DOCUMENT_SMART_LINK',
    urlRegexString: '(^| )https://docs.google.com/document[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'GOOGLE_DOCS_SHEET_SMART_LINK',
    urlRegexString: '(^| )https://docs.google.com/spreadsheets[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'GOOGLE_DOCS_PRESENTATION_SMART_LINK',
    urlRegexString: '(^| )https://docs.google.com/presentation[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'SALESFORCE_SMART_LINK',
    urlRegexString: '[^ ]*lightning.force.com/lightning/r/Account[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'SALESFORCE_SMART_LINK',
    urlRegexString: '[^ ]*lightning.force.com/lightning/r/Opportunity[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'JIRA_ISSUE_SMART_LINK',
    urlRegexString: 'http[^ ]*.atlassian.net/(browse/|(secure/RapidBoard[^ ]*selectedIssue))[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'HUBSPOT_SMART_LINK',
    urlRegexString: '(^| )https://app.hubspot.com/contacts/[^ ]*/deal/[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'FIGMA_SMART_LINK',
    urlRegexString: '(^| )https://www.figma.com/file/[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'ONEDRIVE_SMART_LINK',
    urlRegexString: '[^ ]*sharepoint.com/[^ ]*sourcedoc=[^ ]*',
  },
  {
    linkAnchor: null,
    entityType: 'ASANA_TASK_SMART_LINK',
    urlRegexString: '(^| )https://app.asana.com/0/[^ ]*/[^ ]*',
  },
];

const smartLinkStrategy = type => (contentBlock, callback, contentState) => {
  contentBlock.findEntityRanges(character => {
    const entityKey = character.getEntity();
    return entityKey !== null && contentState.getEntity(entityKey).getType() === type;
  }, callback);
};

const smartLinksPlugin = {
  decorators: [
    {
      strategy: smartLinkStrategy('SALESFORCE_SMART_LINK'),
      component: props => <SalesForceLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('GOOGLE_DOCS_DOCUMENT_SMART_LINK'),
      component: props => <GoogleDocsLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('GOOGLE_DOCS_SHEET_SMART_LINK'),
      component: props => <GoogleSheetsLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('GOOGLE_DOCS_PRESENTATION_SMART_LINK'),
      component: props => <GooglePresentationLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('JIRA_ISSUE_SMART_LINK'),
      component: props => <JiraIssueLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('HUBSPOT_SMART_LINK'),
      component: props => <HubspotLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('FIGMA_SMART_LINK'),
      component: props => <FigmaLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('ONEDRIVE_SMART_LINK'),
      component: props => <OnedriveLink {...props} />,
    },
    {
      strategy: smartLinkStrategy('ASANA_TASK_SMART_LINK'),
      component: props => <AsanaTaskLink {...props} />,
    },
  ],
};

function findWithRegex(regexString, contentBlock, callback) {
  const text = contentBlock.getText();
  const regex = new RegExp(regexString, 'g');
  let matchArr;
  let start;
  let end;
  let linkHref;
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;
    end = start + matchArr[0].length;
    linkHref = matchArr[0];
    callback(start, end, linkHref);
  }
}

function retrieveEntityTypeByRegex(regexString) {
  const schemaItem = smartLinksSchema.find(({ urlRegexString }) => urlRegexString === regexString);
  return schemaItem.entityType;
}

function retrieveLinkAnchorByRegex(regexString) {
  const schemaItem = smartLinksSchema.find(({ urlRegexString }) => urlRegexString === regexString);
  return schemaItem.linkAnchor;
}

function removeCharsAt(str, indexStart, indexEnd) {
  return str.substring(0, indexStart) + ''.padStart(indexEnd - indexStart, ' ') + str.substring(indexEnd);
}

function isTargetUrlExict(selectedBlock) {
  let text = selectedBlock.getText();
  // To prevent it from matching on text that is inside an entity already, we replace
  // text enclosed by an entity with spaces
  selectedBlock.findEntityRanges(
    character => character.getEntity() !== null,
    (start, end) => {
      text = removeCharsAt(text, start, end);
    }
  );
  return Boolean(smartLinksSchema.some(({ urlRegexString }) => new RegExp(urlRegexString, 'g').exec(text) !== null));
}

// function addEntity(contentState, selection, entityType, data) {
//   const contentStateWithEntity = contentState.createEntity(
//     entityType,
//     'IMMUTABLE',
//     data,
//   );
//   const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
//   return contentStateWithAppliedEntity(contentState, selection, entityKey);
// }

async function getDocName(url) {
  try {
    const response = await axios.get(`/api/v0/smart_links/enrich_url?url=${encodeURIComponent(url)}`);
    if (!response && !response.data) return null;
    return response.data.name;
  } catch (e) {
    return null;
  }
}

function replaceWithAnchor(
  contentState,
  start,
  end,
  urlRegexString,
  selectedBlockKey,
  classComponent,
  href,
  editorStateKey
) {
  const selectionForReplace = createEmptySelectionState(selectedBlockKey, start, end);
  const entityType = retrieveEntityTypeByRegex(urlRegexString);
  const linkAnchor = retrieveLinkAnchorByRegex(urlRegexString) || href;
  const contentStateWithAnchor = contentStateWithReplacedText(contentState, selectionForReplace, linkAnchor);

  let selectionToAddEntity;
  const blockForAddingEntity = contentStateWithAnchor.getBlockForKey(selectedBlockKey);
  findWithRegex(linkAnchor.replace('?', '\\?'), blockForAddingEntity, (startEntity, endEntity) => {
    selectionToAddEntity = createEmptySelectionState(selectedBlockKey, startEntity, endEntity);
  });

  const contentStateWithEntity = getContentStateWithAppliedEntity(
    contentStateWithAnchor,
    selectionToAddEntity,
    entityType,
    { href }
  );
  const selectionForWhiteSpace = createEmptySelectionState(
    selectedBlockKey,
    selectionToAddEntity.getEndOffset(),
    selectionToAddEntity.getEndOffset()
  );
  const contenstStateWithWhiteSpace = contentStateWithInsertedText(contentStateWithEntity, selectionForWhiteSpace, ' ');

  classComponent.setState(prevState => {
    const prevEditorState = prevState[editorStateKey];
    const editorState = EditorState.moveFocusToEnd(EditorState.push(prevEditorState, contenstStateWithWhiteSpace));

    return { [editorStateKey]: editorState };
  });
  return { contentStateWithEntity, selectionToAddEntity, entityType };
}

async function replaceWithDocName(entityInfo, linkHref, selectedBlockKey, classComponent, editorStateKey) {
  const docName = await getDocName(linkHref);
  if (!docName) return;
  const { contentStateWithEntity, selectionToAddEntity, entityType } = entityInfo;

  const contentStateWithDocName = contentStateWithReplacedText(contentStateWithEntity, selectionToAddEntity, docName);
  const startEntity = selectionToAddEntity.getAnchorOffset();
  const endEntity = startEntity + docName.length;
  const selectionToAddDocNameEntity = createEmptySelectionState(selectedBlockKey, startEntity, endEntity);

  const contentStateWithDocNameEntity = getContentStateWithAppliedEntity(
    contentStateWithDocName,
    selectionToAddDocNameEntity,
    entityType,
    { href: linkHref }
  );
  const selectionForWhiteSpace = createEmptySelectionState(
    selectedBlockKey,
    selectionToAddDocNameEntity.getEndOffset(),
    selectionToAddDocNameEntity.getEndOffset()
  );
  const contenstStateWithWhiteSpace = contentStateWithInsertedText(
    contentStateWithDocNameEntity,
    selectionForWhiteSpace,
    ' '
  );
  classComponent.setState(prevState => {
    const prevEditorState = prevState[editorStateKey];
    const editorState = EditorState.moveFocusToEnd(EditorState.push(prevEditorState, contenstStateWithWhiteSpace));
    return { [editorStateKey]: editorState };
  });
}

function applySectionHeaderStyling(editorState, applyStyling) {
  if (!applyStyling) return editorState;

  const contentState = editorState.getCurrentContent();

  const firstBlockKey = contentState.getBlockMap().first().getKey();
  const selectedStartBlockKey = editorState.getSelection().getStartKey();
  const isFirstBlock = selectedStartBlockKey === firstBlockKey;
  const currentBlockType = RichUtils.getCurrentBlockType(editorState);
  const isHeading = currentBlockType === SECTION_HEADER_TITLE_TYPE;
  if (isFirstBlock !== isHeading) return RichUtils.toggleBlockType(editorState, SECTION_HEADER_TITLE_TYPE);

  return editorState;
}

async function modifyUrlBySmartLink(editorState, classComponent, editorStateKey = 'editorState') {
  const contentState = editorState.getCurrentContent();
  const selectedBlockKey = editorState.getSelection().getAnchorKey();
  const selectedBlock = contentState.getBlockForKey(selectedBlockKey);

  if (!isTargetUrlExict(selectedBlock)) return classComponent.setState({ [editorStateKey]: editorState });

  smartLinksSchema.forEach(({ urlRegexString }) => {
    findWithRegex(urlRegexString, selectedBlock, async (start, end, linkHref) => {
      const entityInfo = replaceWithAnchor(
        contentState,
        start,
        end,
        urlRegexString,
        selectedBlockKey,
        classComponent,
        linkHref,
        editorStateKey
      );
      replaceWithDocName(entityInfo, linkHref, selectedBlockKey, classComponent, editorStateKey);
    });
  });

  return null;
}

// This is a temporary hack to deal with the fact that creating a contentState from a smartlink compatable URL string
// seems to break the smart link decorator.  To bypass that issue, this function returns the content state of a smart
// link that is already decorated.  Since this is only currently used by surface, it also takes the doc name (linkText)
// as well to drop in so we don't need to do an API call in order to fetch the doc name.
//
// This should be removed and replaced with something more robust whenever the smart link code gets re-written.
function contentStateFromLink({ linkText, linkHref }) {
  const type = smartLinksSchema.find(
    ({ urlRegexString }) => new RegExp(urlRegexString, 'g').exec(linkHref) !== null
  ).entityType;
  return {
    blocks: [
      {
        key: genKey(),
        text: linkText,
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [{ offset: 0, length: linkText.length, key: 0 }],
        data: {},
      },
    ],
    entityMap: {
      0: {
        type,
        mutability: 'IMMUTABLE',
        data: { href: linkHref },
      },
    },
  };
}

export { smartLinksPlugin, modifyUrlBySmartLink, applySectionHeaderStyling, contentStateFromLink };
